import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import { formatInTimeZone } from "~/lib/utils";
import type { postiesTodaysReadContentItemContent$key } from "~/queries/__generated__/postiesTodaysReadContentItemContent.graphql";

import {
  Container,
  Content,
  FlagContainer,
  FlagText,
  Headline,
  ImageWrapper,
  Section,
  StyledButton,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledFlag,
  StyledImageEntityLink,
  StyledOffsetContainer,
} from "./styles";

export type Props = {
  reference: postiesTodaysReadContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemPostiesTodaysRead = forwardRef<HTMLDivElement, Props>(
  ({ className, customQueryParameters, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment postiesTodaysReadContentItemContent on Content {
          ...entityLink
          ...contentCoverImageContent
          sections {
            value {
              name
              urlAlias
            }
          }
        }
      `,
      reference_,
    );
    const section = last(first(content?.sections)?.value);
    const day = formatInTimeZone(new Date(), "d");
    const month = formatInTimeZone(new Date(), "MMM");

    return (
      <Container className={className} ref={reference}>
        <ImageWrapper>
          <StyledImageEntityLink query={customQueryParameters} reference={content}>
            <StyledOffsetContainer borderRadius="20px">
              <StyledContentCoverImage
                reference={content}
                responsiveVariants={{
                  desktopUp: "size1200x800",
                  mobileUp: "size540x360",
                  tabletUp: "size540x360",
                }}
              />
              <FlagContainer>
                <StyledFlag />
                <FlagText>
                  {month}
                  <span>{day}</span>
                </FlagText>
              </FlagContainer>
            </StyledOffsetContainer>
          </StyledImageEntityLink>
        </ImageWrapper>

        <Content>
          {notEmpty(section) && <Section pathname={section.urlAlias}>{section?.name}</Section>}
          <StyledEntityLink query={customQueryParameters} reference={content}>
            <Headline>
              {provider.headline({
                hideTag: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            <StyledButton variant="button">READ MORE</StyledButton>
          </StyledEntityLink>
        </Content>
      </Container>
    );
  },
);

ContentItemPostiesTodaysRead.displayName = "ContentItemPostiesTodaysRead";
